import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { TextField, InputAdornment, IconButton } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import VideocamIcon from '@mui/icons-material/Videocam'

const SearchPage = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const navigate = useNavigate()

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      // Navigate to database page with search query
      navigate(`/database?search=${encodeURIComponent(searchQuery)}`)
    }
  }

  const floatingIcons = [
    { icon: '📹', delay: 0, x: -100, y: -50 },
    { icon: '🎥', delay: 0.2, x: 100, y: -80 },
    { icon: '📷', delay: 0.4, x: -80, y: 60 },
    { icon: '🔍', delay: 0.6, x: 120, y: 40 },
  ]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="flex items-center justify-center min-h-screen relative overflow-hidden"
    >
      {/* Background animated elements */}
      <div className="absolute inset-0 overflow-hidden">
        {floatingIcons.map((item, index) => (
          <motion.div
            key={index}
            className="absolute text-6xl opacity-10"
            initial={{ x: item.x, y: item.y, opacity: 0, scale: 0 }}
            animate={{
              x: item.x,
              y: item.y,
              opacity: [0, 0.1, 0.05],
              scale: [0, 1.2, 1],
              rotate: [0, 360],
            }}
            transition={{
              delay: item.delay,
              duration: 3,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut',
            }}
            style={{
              left: '50%',
              top: '50%',
            }}
          >
            {item.icon}
          </motion.div>
        ))}
      </div>

      {/* Main content */}
      <div className="z-10 w-full max-w-3xl px-6">
        {/* Logo/Title */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 100 }}
          className="text-center mb-12"
        >
          <motion.div
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="inline-block"
          >
            <VideocamIcon
              sx={{ fontSize: 80, color: '#3b82f6', marginBottom: 2 }}
            />
          </motion.div>
          <motion.h1
            className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent animate-gradient"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Camera Lookup
          </motion.h1>
          <motion.p
            className="text-gray-400 mt-4 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Find the perfect CCTV camera for your needs
          </motion.p>
        </motion.div>

        {/* Search field */}
        <motion.form
          onSubmit={handleSearch}
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, type: 'spring', stiffness: 100 }}
        >
          <motion.div
            animate={
              isFocused
                ? {
                    scale: 1.02,
                    boxShadow: '0 0 30px rgba(59, 130, 246, 0.5)',
                  }
                : {
                    scale: 1,
                    boxShadow: '0 0 15px rgba(59, 130, 246, 0.2)',
                  }
            }
            transition={{ duration: 0.3 }}
            className="rounded-2xl"
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search camera models, manufacturers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '1.25rem',
                  padding: '8px',
                  backgroundColor: 'rgba(26, 26, 26, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '1rem',
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                    borderWidth: 2,
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(59, 130, 246, 0.6)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 500 }}
                    >
                      <IconButton
                        type="submit"
                        sx={{
                          color: '#3b82f6',
                          '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          },
                        }}
                      >
                        <SearchIcon />
                      </IconButton>
                    </motion.div>
                  </InputAdornment>
                ),
              }}
            />
          </motion.div>
        </motion.form>

        {/* Quick stats or info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 text-center"
        >
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-gray-500 text-sm"
          >
            Press Enter or click the search icon to search
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  )
}

export default SearchPage
